import { Injectable } from '@angular/core';
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../service/auth/auth.service';
import { DashboardLayoutService } from 'src/app/modules/dashboard/services/dashboard-layout.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    private static readonly ADMIN_API = /\/api\/(admin|cms)\//i;

    private isLoggingOut = false;

    constructor(
        private router: Router,
        private authService: AuthService
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // SD-1230: the admin and CMS APIs refuse a cookie-authenticated write without this header.
        // A cross-site page cannot add a custom header without a CORS preflight, which the API
        // only grants to our own origins -- so the header is the CSRF check.
        const clonedRequest = request.clone({
            withCredentials: true,
            setHeaders: AuthInterceptor.ADMIN_API.test(request.url) ? { 'X-Synced-Admin': '1' } : {}
        });
        return next.handle(clonedRequest).pipe(
            catchError(error => {
                if (error.status === 401) {

                    // ✅ Prevent re-interception of the check endpoint
                    if (request.url.includes('Account/me') || request.url.includes('token/logout')) {
                        this.performLocalCleanup();
                        this.toLogin();
                        return throwError(error);
                    }

                    return this.authService.check().pipe(
                        switchMap(isAuthenticated => {
                            if (!isAuthenticated) {
                                this.toLogin();
                            }
                            return throwError(error);
                        })
                    );
                }
                return throwError(error);
            })
        );
    }

    /**
     * To the login page, remembering where the user was going (?redirectURL=, as AuthGuard does),
     * so a link opened while signed out -- a practice invitation, say -- still lands after login.
     * The address bar is read rather than router.url, which is still the previous page while the
     * first navigation is in progress.
     */
    private toLogin(): void {
        // Already on a sign-in page (AuthGuard got there first, with its ?redirectURL=): stay put.
        if (window.location.pathname.startsWith('/auth/')) return;
        // The invitation page is public and asks whether a session is live itself; signed out, it
        // offers to sign in or join. Sent to log in from there, the login page hands an invitation
        // straight back to it -- the two flicked between each other on a stale session.
        if (window.location.pathname.startsWith('/practice-join/')) return;
        const here = window.location.pathname + window.location.search;
        this.router.navigate(['auth/login'], here !== '/' ? { queryParams: { redirectURL: here } } : {});
    }

    private performLocalCleanup(): void {
        if (this.isLoggingOut) return; // Prevent duplicate cleanup calls
        this.isLoggingOut = true;

        // Clear all localStorage keys
        localStorage.removeItem('AccessToken');
        localStorage.removeItem('UpUserId');
        localStorage.removeItem('SelectedOrganization');
        localStorage.removeItem('SelectedOrganizationName');
        localStorage.removeItem('UpUserDetails');
        localStorage.removeItem('accountRemember');
        localStorage.removeItem('UserName');
        localStorage.removeItem('UserId');
        localStorage.removeItem('Id');
        localStorage.removeItem('InvoceIdsStorage');
        localStorage.removeItem('IsLogedin');
        localStorage.removeItem('IsXeroDirect');

        // SD-1209: the dashboard layout cache is keyed by user and organisation, so it cannot
        // be named in the list above -- sweep it by prefix.
        DashboardLayoutService.clearAllCaches();

        sessionStorage.clear();

        this.isLoggingOut = false;
    }
}
